import { useEffect, useRef, useState } from 'react';

export interface UseInViewOptions {
  /** Distance before the element enters the viewport to trigger. Default: '200px' */
  rootMargin?: string;
  /** Skip observation and immediately return true. Useful for non-homepage usage. */
  skip?: boolean;
}

export interface UseInViewResult {
  /** Ref to attach to the observed container element */
  ref: React.RefObject<HTMLDivElement | null>;
  /** Flips to true once the element enters rootMargin range; never reverts */
  isInView: boolean;
}

/**
 * Observes an element's proximity to the viewport. Once the element enters
 * the rootMargin range, isInView flips to true permanently (one-shot).
 *
 * Falls back to isInView=true if IntersectionObserver is unavailable.
 */
export function useInView({
  rootMargin = '200px',
  skip = false,
}: UseInViewOptions = {}): UseInViewResult {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(skip);
  const triggeredRef = useRef(skip);

  useEffect(() => {
    if (triggeredRef.current) {
      return;
    }

    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      triggeredRef.current = true;
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          triggeredRef.current = true;
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin, skip]);

  return { ref, isInView };
}
